import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Blocks,
  Briefcase,
  Calendar,
  ClipboardCheck,
  FileText,
  GraduationCap,
  Hammer,
  Home,
  Inbox,
  Megaphone,
  MessageSquare,
  Settings,
  Sparkles,
  Target,
  Wand2,
} from 'lucide-react';

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  phase: number;
  enabled: boolean;
  /** Other top-level paths this item owns — it lights up on all of them. */
  matchPaths?: string[];
  /**
   * Active on its own path only, not on paths below it. תבניות פרומפט lives at
   * /prompts and בונה פרומפטים at /prompts/build; without this both would light
   * up on the builder.
   */
  exact?: boolean;
  /**
   * The page opens and is honest about being thin. It stays in the sidebar
   * with a "בבנייה" badge rather than being hidden: a page nobody can find is
   * indistinguishable from a page that was deleted, which is exactly the
   * report this flag exists to prevent.
   */
  wip?: boolean;
}

export interface NavSection {
  id: string;
  /** null = no heading (the top group). */
  label: string | null;
  collapsible: boolean;
  items: NavItem[];
}

/**
 * Everything about one client lives behind a single sidebar entry. These are
 * the pages it owns, in the order the sub-navigation shows them. Paths did not
 * move — bookmarks, Ctrl+K and links all keep working.
 */
export const CLIENT_TABS: { path: string; label: string }[] = [
  { path: '/customers', label: 'לקוחות' },
  { path: '/quotes', label: 'הצעות מחיר' },
  { path: '/projects', label: 'פרויקטים' },
  { path: '/tasks', label: 'משימות' },
  { path: '/time', label: 'שעות' },
  { path: '/finance', label: 'כספים' },
];

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'top',
    label: null,
    collapsible: false,
    items: [
      { id: 'home', label: 'דף הבית', icon: Home, path: '/', phase: 0, enabled: true },
      { id: 'inbox', label: 'אינבוקס', icon: Inbox, path: '/inbox', phase: 2, enabled: true },
    ],
  },
  {
    id: 'sales',
    label: 'מכירות',
    collapsible: false,
    items: [
      { id: 'leads', label: 'לידים', icon: Sparkles, path: '/leads', phase: 1, enabled: true },
      { id: 'bot', label: 'בוט', icon: MessageSquare, path: '/bot', phase: 1, enabled: true },
    ],
  },
  {
    id: 'clients',
    label: 'לקוחות ופרויקטים',
    collapsible: false,
    items: [
      {
        id: 'client-management',
        label: 'ניהול לקוח',
        icon: Briefcase,
        path: '/customers',
        phase: 1,
        enabled: true,
        matchPaths: CLIENT_TABS.map((t) => t.path),
      },
    ],
  },
  {
    id: 'business',
    label: 'העסק',
    collapsible: false,
    items: [
      { id: 'focus', label: 'פוקוס', icon: Target, path: '/focus', phase: 1, enabled: true },
      {
        id: 'governance',
        label: 'ממשל',
        icon: ClipboardCheck,
        path: '/governance',
        phase: 0,
        enabled: true,
      },
      { id: 'docs', label: 'מסמכים', icon: FileText, path: '/docs', phase: 5, enabled: true },
      {
        id: 'analytics',
        label: 'אנליטיקה',
        icon: BarChart3,
        path: '/analytics',
        phase: 5,
        enabled: true,
      },
      {
        id: 'marketing',
        label: 'שיווק',
        icon: Megaphone,
        path: '/marketing',
        phase: 4,
        enabled: true,
        wip: true,
      },
      {
        id: 'booking',
        label: 'יומן',
        icon: Calendar,
        path: '/booking',
        phase: 5,
        enabled: true,
        wip: true,
      },
    ],
  },
  {
    // The academy and everything that grew out of it: the lessons, the prompt
    // templates the lessons wrote, the builder that composes new ones, and the
    // Skills library. One heading, four pages — reported missing twice while
    // they sat under "סטודיו" next to ממשל, which is not academy material.
    id: 'academy',
    label: 'אקדמיה',
    collapsible: true,
    items: [
      {
        id: 'academy',
        label: 'נושאים',
        icon: GraduationCap,
        path: '/academy',
        phase: 0,
        enabled: true,
      },
      {
        id: 'prompts',
        label: 'תבניות פרומפט',
        icon: Wand2,
        path: '/prompts',
        phase: 0,
        enabled: true,
        exact: true,
      },
      {
        id: 'prompt-builder',
        label: 'בונה פרומפטים',
        icon: Hammer,
        path: '/prompts/build',
        phase: 0,
        enabled: true,
      },
      { id: 'skills', label: 'Skills', icon: Blocks, path: '/skills', phase: 0, enabled: true },
    ],
  },
];

/** The section an item sits in, for breadcrumbs. */
export function findNavSection(item: NavItem): NavSection | undefined {
  return NAV_SECTIONS.find((s) => s.items.includes(item));
}

/**
 * Nothing is hidden from the sidebar any more. שיווק and יומן used to live
 * here, reachable only from Ctrl+K, and the result was a bug report that the
 * pages had been deleted. A thin page carries `wip` and stays visible instead.
 * Kept as an empty export so `findNavItemByPath` keeps one shape.
 */
export const HIDDEN_NAV: NavItem[] = [];

export const PRIMARY_NAV: NavItem[] = NAV_SECTIONS.flatMap((s) => s.items);

export const FOOTER_NAV: NavItem[] = [
  { id: 'settings', label: 'הגדרות', icon: Settings, path: '/settings', phase: 0, enabled: true },
];

function pathOwns(owner: string, path: string): boolean {
  return path === owner || path.startsWith(owner + '/');
}

/** The deepest owned path of `item` that matches, or null. */
function matchLength(item: NavItem, path: string): number | null {
  if (item.exact) return path === item.path ? item.path.length : null;
  const candidates = [item.path, ...(item.matchPaths ?? [])].filter((p) => pathOwns(p, path));
  if (candidates.length === 0) return null;
  return Math.max(...candidates.map((p) => p.length));
}

export function isNavItemActive(item: NavItem, path: string): boolean {
  if (item.path === '/' && !item.matchPaths) return path === '/';
  return matchLength(item, path) !== null;
}

export function findNavItemByPath(path: string): NavItem | undefined {
  const all = [...PRIMARY_NAV, ...HIDDEN_NAV, ...FOOTER_NAV];
  // Prefer most specific (longest owned path) match.
  return all
    .map((item) => ({ item, len: matchLength(item, path) }))
    .filter((m): m is { item: NavItem; len: number } => m.len !== null)
    .sort((a, b) => b.len - a.len)[0]?.item;
}

export function findClientTab(path: string): { path: string; label: string } | undefined {
  return CLIENT_TABS.find((t) => pathOwns(t.path, path));
}
